import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import AppHeader from "@/components/AppHeader";
import CommunityBoardMap from "@/components/CommunityBoardMap";
import CommunityEditorDialog from "@/components/CommunityEditorDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  ChevronLeft, UserPlus, Users, LogOut, Settings, Trash2,
  Check, X, Search, Shield, UserMinus, Globe, Lock, Loader2,
} from "lucide-react";
import api from "@/lib/api";
import { maskEmail } from "@/lib/mask";
import { displayName } from "@/lib/displayName";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const DEFAULT_THEME = { join: "#0A84FF", settings: "#12466F", leave: "#EF4444" };

/**
 * Dedicated page (was previously a modal) for a single community.
 *  - Hero banner (bg_image_url or theme gradient fallback)
 *  - Join / Leave / Delete actions
 *  - Moderator (creator) controls: invite by name/email, approve/reject
 *    pending requests, **remove members**, open the editor to change
 *    settings/banner.
 */
const CommunityDetail = () => {
  const { cid } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [editorOpen, setEditorOpen] = useState(false);
  const [toRemove, setToRemove] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const [inviteQ, setInviteQ] = useState("");
  const [inviteResults, setInviteResults] = useState([]);
  const [inviting, setInviting] = useState("");

  const load = async () => {
    try {
      const r = await api.get(`/communities/${cid}`);
      setDetail(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Community non trovata");
      navigate("/community", { replace: true });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [cid]);

  useEffect(() => {
    const q = inviteQ.trim();
    if (q.length < 2) { setInviteResults([]); return; }
    const t = setTimeout(async () => {
      try { const r = await api.get("/users/search", { params: { q } }); setInviteResults(r.data || []); }
      catch { setInviteResults([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [inviteQ]);

  if (loading || !detail) {
    return (
      <div className="min-h-screen">
        <AppHeader />
        <main className="max-w-5xl mx-auto p-6">
          <div className="py-16 text-center text-slate-500">Caricamento…</div>
        </main>
      </div>
    );
  }

  const theme = detail.theme || DEFAULT_THEME;
  const um = detail.users_map || {};
  const isModerator = !!detail.is_moderator || !!detail.is_owner || user?.role === "admin";
  const isMember = !!detail.is_member;

  const wrap = (label, fn) => async () => {
    setBusy(label);
    try { await fn(); await load(); toast.success("Fatto"); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBusy(""); }
  };

  const join    = wrap("join",   async () => { await api.post(`/communities/${cid}/join`); });
  const leave   = wrap("leave",  async () => { await api.post(`/communities/${cid}/leave`); });
  const approve = (uid) => wrap(`approve-${uid}`, async () => { await api.post(`/communities/${cid}/approve`, { user_id: uid }); })();
  const reject  = (uid) => wrap(`reject-${uid}`,  async () => { await api.post(`/communities/${cid}/reject`,  { user_id: uid }); })();

  const invite = async (uid) => {
    setInviting(uid);
    try {
      await api.post(`/communities/${cid}/invite`, { user_id: uid });
      toast.success("Utente aggiunto");
      setInviteQ(""); setInviteResults([]);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setInviting(""); }
  };

  const doRemove = async () => {
    if (!toRemove) return;
    setBusy(`remove-${toRemove.user_id}`);
    try {
      await api.delete(`/communities/${cid}/members/${toRemove.user_id}`);
      toast.success(`${toRemove.name || toRemove.email} rimosso`);
      setToRemove(null);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBusy(""); }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/communities/${cid}`);
      toast.success("Community eliminata");
      navigate("/community", { replace: true });
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  return (
    <div className="min-h-screen bg-slate-50/50">
      <AppHeader />

      {/* Hero banner ------------------------------------------------------- */}
      <div
        className="relative w-full"
        style={{
          height: 260,
          background: detail.bg_image_url
            ? `url(${detail.bg_image_url}) center/cover no-repeat`
            : `linear-gradient(135deg, ${theme.settings}, ${theme.join})`,
        }}
        data-testid="community-hero"
      >
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/10" />
        <div className="relative max-w-5xl mx-auto h-full px-6 flex flex-col justify-between py-5">
          <button
            onClick={() => navigate("/community")}
            className="self-start inline-flex items-center gap-1.5 rounded-full bg-white/85 hover:bg-white px-3 py-1.5 text-xs font-semibold text-slate-800 backdrop-blur transition-colors"
            data-testid="community-back-btn"
          >
            <ChevronLeft className="w-3.5 h-3.5" /> Torna alle Community
          </button>
          <div className="text-white">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/20 backdrop-blur px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold">
              {detail.is_public ? <><Globe className="w-3 h-3" /> Pubblica</> : <><Lock className="w-3 h-3" /> Su richiesta</>}
            </div>
            <h1 className="font-display font-black text-4xl md:text-5xl tracking-tight leading-none mt-2 drop-shadow-lg" data-testid="community-name">{detail.name}</h1>
            <div className="text-sm mt-2 text-white/90">
              {detail.member_count} membri
              {detail.pending_count ? ` · ${detail.pending_count} in attesa` : ""}
            </div>
          </div>
        </div>
      </div>

      <main className="max-w-5xl mx-auto p-6 space-y-5">
        {/* Action bar --------------------------------------------------- */}
        <div className="ios-card p-4 flex flex-wrap gap-2 items-center">
          {!isMember && !detail.has_pending_request && (
            <Button onClick={join} disabled={!!busy} className="rounded-full text-white" style={{ background: theme.join }} data-testid="community-join">
              <UserPlus className="w-4 h-4 mr-1.5" /> {detail.is_public ? "Partecipa" : "Richiedi di partecipare"}
            </Button>
          )}
          {detail.has_pending_request && (
            <div className="rounded-full px-3 py-1.5 text-xs bg-amber-50 text-amber-700 border border-amber-200 font-semibold">Richiesta inviata — in attesa</div>
          )}
          {isMember && !isModerator && (
            <Button onClick={leave} disabled={!!busy} className="rounded-full text-white" style={{ background: theme.leave }} data-testid="community-leave">
              <LogOut className="w-4 h-4 mr-1.5" /> Abbandona
            </Button>
          )}
          {isModerator && (
            <>
              <div className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 border border-amber-200 text-amber-800 text-[10px] font-bold uppercase tracking-widest px-2.5 py-1">
                <Shield className="w-3 h-3" /> Sei il moderatore
              </div>
              <Button onClick={() => setEditorOpen(true)} className="rounded-full text-white" style={{ background: theme.settings }} data-testid="community-settings-btn">
                <Settings className="w-4 h-4 mr-1.5" /> Impostazioni & Banner
              </Button>
              <Button variant="ghost" onClick={() => setConfirmDelete(true)} className="rounded-full text-rose-600 hover:bg-rose-50" data-testid="community-delete">
                <Trash2 className="w-4 h-4 mr-1.5" /> Elimina
              </Button>
            </>
          )}
        </div>

        {/* Description -------------------------------------------------- */}
        {detail.description && (
          <div className="ios-card p-5">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Descrizione</div>
            <p className="text-sm text-slate-700 whitespace-pre-wrap">{detail.description}</p>
          </div>
        )}

        {/* Invite panel (moderator only) -------------------------------- */}
        {isModerator && (
          <div className="ios-card p-5">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2 flex items-center gap-1.5">
              <UserPlus className="w-3 h-3" /> Aggiungi membri
            </div>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                value={inviteQ}
                onChange={(e) => setInviteQ(e.target.value)}
                placeholder="Cerca per nome o email…"
                className="rounded-xl pl-9 bg-slate-50 h-10"
                data-testid="community-invite-search"
              />
            </div>
            {inviteResults.length > 0 && (
              <div className="mt-2 rounded-xl bg-white border border-slate-200 max-h-56 overflow-y-auto shadow-sm" data-testid="community-invite-results">
                {inviteResults
                  .filter((u) => !(detail.member_ids || []).includes(u.user_id))
                  .map((u) => (
                    <button
                      key={u.user_id}
                      onClick={() => invite(u.user_id)}
                      disabled={!!inviting}
                      className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left hover:bg-slate-50 text-sm border-b last:border-b-0 border-slate-100"
                      data-testid={`community-invite-add-${u.user_id}`}
                    >
                      <span className="flex items-center gap-2 min-w-0">
                        {u.picture ? (
                          <img src={u.picture} alt="" className="w-7 h-7 rounded-full" />
                        ) : (
                          <div className="w-7 h-7 rounded-full bg-slate-200 text-[11px] font-semibold text-slate-600 flex items-center justify-center">
                            {(u.name || u.email || "?")[0]?.toUpperCase()}
                          </div>
                        )}
                        <span className="truncate">
                          <span className="font-medium">{displayName(u) || u.name || u.email}</span>
                          <span className="block text-[10px] text-slate-500">{maskEmail(u.email)}</span>
                        </span>
                      </span>
                      <span className="text-[10px] uppercase tracking-widest font-bold shrink-0" style={{ color: theme.join }}>
                        {inviting === u.user_id ? <Loader2 className="w-3 h-3 animate-spin" /> : "+ Aggiungi"}
                      </span>
                    </button>
                  ))}
              </div>
            )}
            {inviteQ.length >= 2 && inviteResults.length === 0 && (
              <div className="mt-1.5 text-[11px] text-slate-500 px-2">Nessun utente trovato.</div>
            )}
          </div>
        )}

        {/* Pending requests (moderator only) --------------------------- */}
        {isModerator && (detail.join_requests || []).length > 0 && (
          <div className="ios-card p-5">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-3">
              Richieste in attesa ({detail.join_requests.length})
            </div>
            <div className="space-y-1.5">
              {detail.join_requests.map((uid) => {
                const u = um[uid] || { name: uid };
                return (
                  <div key={uid} className="flex items-center justify-between rounded-xl bg-slate-50 px-3 py-2">
                    <span className="text-sm truncate flex items-center gap-2">
                      {u.picture ? <img src={u.picture} alt="" className="w-6 h-6 rounded-full" /> : <div className="w-6 h-6 rounded-full bg-slate-200 text-[10px] flex items-center justify-center font-semibold text-slate-600">{(u.name || "?")[0]}</div>}
                      <span>
                        <span className="font-medium">{displayName(u) || u.name || u.email}</span>
                        <span className="block text-[10px] text-slate-500">{maskEmail(u.email)}</span>
                      </span>
                    </span>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" className="rounded-full h-8 w-8 p-0 text-emerald-600 hover:bg-emerald-50" onClick={() => approve(uid)} data-testid={`community-approve-${uid}`}>
                        <Check className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" className="rounded-full h-8 w-8 p-0 text-rose-600 hover:bg-rose-50" onClick={() => reject(uid)} data-testid={`community-reject-${uid}`}>
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Members list ------------------------------------------------ */}
        <div className="ios-card p-5">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-3 flex items-center gap-1.5">
            <Users className="w-3 h-3" /> Membri ({(detail.member_ids || []).length})
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="community-members-grid">
            {(detail.member_ids || []).map((uid) => {
              const u = um[uid] || { name: uid };
              const isCreator = uid === detail.created_by;
              const canRemove = isModerator && !isCreator && uid !== user?.user_id;
              return (
                <div key={uid} className="flex items-center justify-between rounded-xl bg-slate-50 px-3 py-2" data-testid={`community-member-${uid}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    {u.picture ? (
                      <img src={u.picture} alt="" className="w-8 h-8 rounded-full" />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-slate-300 text-xs font-semibold text-slate-600 flex items-center justify-center">
                        {(u.name || u.email || "?")[0]?.toUpperCase()}
                      </div>
                    )}
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate flex items-center gap-1.5">
                        {displayName(u) || u.name || u.email || uid}
                        {isCreator && (
                          <span className="inline-flex items-center gap-0.5 rounded-full bg-amber-100 text-amber-700 text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5">
                            <Shield className="w-2.5 h-2.5" /> Moderatore
                          </span>
                        )}
                      </div>
                      <div className="text-[10px] text-slate-500 truncate">{maskEmail(u.email) || uid}</div>
                    </div>
                  </div>
                  {canRemove && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setToRemove({ user_id: uid, name: u.name, email: u.email })}
                      disabled={busy === `remove-${uid}`}
                      className="rounded-full h-8 px-2 text-rose-600 hover:bg-rose-50 shrink-0"
                      data-testid={`community-remove-${uid}`}
                      title="Rimuovi membro"
                    >
                      <UserMinus className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Boards map -------------------------------------------------- */}
        <div className="ios-card p-5">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-3">Schede sul territorio</div>
          <CommunityBoardMap communityId={detail.id} />
        </div>
      </main>

      <CommunityEditorDialog
        open={editorOpen}
        onOpenChange={setEditorOpen}
        community={detail}
        onSaved={() => load()}
      />

      {/* Remove-member confirm ------------------------------------------ */}
      <AlertDialog open={!!toRemove} onOpenChange={(v) => !v && setToRemove(null)}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Rimuovere questo membro?</AlertDialogTitle>
            <AlertDialogDescription>
              {toRemove?.name || maskEmail(toRemove?.email)} verrà rimosso dalla community. Potrà comunque richiedere di rientrare in seguito.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Annulla</AlertDialogCancel>
            <AlertDialogAction className="rounded-full bg-rose-600 hover:bg-rose-700" onClick={doRemove} data-testid="community-remove-confirm">
              Rimuovi
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete-community confirm --------------------------------------- */}
      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare la community?</AlertDialogTitle>
            <AlertDialogDescription>
              L'azione è definitiva. Tutti i membri perderanno l'accesso a "{detail.name}".
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Annulla</AlertDialogCancel>
            <AlertDialogAction className="rounded-full bg-rose-600 hover:bg-rose-700" onClick={doDelete} data-testid="community-delete-confirm">
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CommunityDetail;
